import { frota, salvarPoliticaDeCustoSchema } from '@locaveri/shared'
import { LoaderCircle } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { formatarDataHora } from '@/lib/datas'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import { type PoliticaDeCusto, usePoliticaDeCusto, useSalvarPoliticaDeCusto } from './api'

/**
 * Política de custo indireto — os quatro parâmetros do Gap 7.
 *
 * ---------------------------------------------------------------------------
 * É AQUI QUE O PAINEL PARA DE MOSTRAR LUCRO QUE NÃO EXISTE
 * ---------------------------------------------------------------------------
 * Sem esta tela, o custo de um veículo é o que saiu do banco — e um blindado de
 * R$ 400 mil que rodou o mês sem uma única despesa aparece com custo zero.
 * Depreciação, custo de capital e overhead são o que aquele mês REALMENTE
 * consumiu, e é o fechamento mensal que os transforma em linha. Estes quatro
 * números são a premissa de todos eles: mexer aqui move o custo da frota
 * inteira no próximo fechamento.
 *
 * As competências JÁ FECHADAS não são reescritas — o que foi gravado com a
 * premissa antiga fica como está. Contabilidade não se recalcula de trás para
 * frente porque alguém salvou um formulário.
 *
 * ---------------------------------------------------------------------------
 * PONTOS-BASE NA API, PERCENTUAL NA TELA — E O BUG DE FATOR 100
 * ---------------------------------------------------------------------------
 * A API guarda taxa em PONTOS-BASE inteiros (`1200` = 12% a.a.), porque 0,12
 * não é representável em binário e uma taxa que "quase" fecha vira centavo
 * perdido multiplicado por doze meses e por toda a frota. A pessoa, por outro
 * lado, pensa em "12% ao ano".
 *
 * Então a conversão acontece NESTE arquivo, nos dois sentidos, em um lugar só —
 * `percentualParaPontosBase` e `formatarPontosBaseComoPercentual`. É o único
 * ponto do painel onde um fator 100 pode nascer, e é por isso que ele está
 * isolado, documentado e feito sobre os DÍGITOS: `0,07 * 100` em ponto
 * flutuante dá 7.000000000000001, e um `Math.round` esquecido aqui viraria uma
 * taxa errada aplicada a todos os veículos, todo mês, sem ninguém perceber.
 */

/** Campos cujo nome coincide com o caminho do schema — os outros dois caem no alerta. */
const CAMPOS = ['vidaUtilEmMeses', 'overheadMensal'] as const

const PONTOS_BASE_POR_PONTO_PERCENTUAL = 100

/**
 * `12`, `12,5` ou `12.34` — até três dígitos inteiros e duas casas.
 *
 * Escrito como ALTERNÂNCIA de dois padrões chatos em vez de um grupo opcional
 * aninhado: `eslint-plugin-security` mede altura de estrela, e um `?` em volta
 * de um `{1,2}` acusa expressão potencialmente exponencial. Aqui não haveria
 * retrocesso de verdade, mas silenciar o plugin por regra caso a caso é pior do
 * que escrever a regex que ele consegue provar segura.
 */
const PERCENTUAL_DIGITADO = /^\d{1,3}$|^\d{1,3}[.,]\d{1,2}$/

/**
 * `1200` → `12`; `1250` → `12,5`; `1234` → `12,34`.
 *
 * Exportado porque a tela de custos mostra a política em uma linha de resumo, e
 * duas implementações da mesma conversão é exatamente como o fator 100 volta.
 */
export function formatarPontosBaseComoPercentual(pontosBase: number): string {
  const inteiro = Math.trunc(pontosBase / PONTOS_BASE_POR_PONTO_PERCENTUAL)
  const centesimos = Math.abs(pontosBase % PONTOS_BASE_POR_PONTO_PERCENTUAL)

  if (centesimos === 0) {
    return String(inteiro)
  }

  // `05` continua `05`; `50` vira `5`. Só o zero à DIREITA é ruído.
  const casas = String(centesimos).padStart(2, '0').replace(/0$/, '')

  return `${String(inteiro)},${casas}`
}

/**
 * `"12"` → `1200`; `"12,5"` → `1250`; `"12,34"` → `1234`. `null` quando não dá
 * para ler ou quando passa de 100% — e `null` vira erro de campo, nunca um zero
 * silencioso.
 *
 * A conta é feita sobre os DÍGITOS da string, e não com `Number(texto) * 100`,
 * pelo motivo do cabeçalho: multiplicar percentual em ponto flutuante é como o
 * erro de fator 100 (ou de um centésimo) entra em produção.
 */
function percentualParaPontosBase(texto: string): number | null {
  const limpo = texto.trim().replace('%', '').trim()

  if (!PERCENTUAL_DIGITADO.test(limpo)) {
    return null
  }

  const [inteiro = '0', decimal = ''] = limpo.split(/[.,]/)
  const pontosBase =
    Number(inteiro) * PONTOS_BASE_POR_PONTO_PERCENTUAL + Number(decimal.padEnd(2, '0'))

  return pontosBase > frota.PONTOS_BASE_MAXIMOS ? null : pontosBase
}

/** Mensagem em minúscula e sem ponto, como o resto dos formulários do painel. */
const PERCENTUAL_INVALIDO = 'percentual inválido — use de 0 a 100, com até duas casas (ex.: 12,5)'

function validarPercentual(valor: string): true | string {
  if (valor.trim().length === 0) {
    return 'informe o percentual'
  }

  return percentualParaPontosBase(valor) === null ? PERCENTUAL_INVALIDO : true
}

interface FormularioDePolitica {
  vidaUtilEmMeses: string
  /** PERCENTUAL digitado (`"20"`), convertido para pontos-base no envio. */
  residualEmPercentual: string
  /** PERCENTUAL ao ano digitado (`"12"`), convertido para pontos-base no envio. */
  custoDeCapitalEmPercentual: string
  /** Reais digitados (`"8.500,00"`), convertidos para centavos no envio. */
  overheadMensal: string
}

function valoresIniciais(politica: PoliticaDeCusto | undefined): FormularioDePolitica {
  return {
    vidaUtilEmMeses: politica === undefined ? '' : String(politica.vidaUtilEmMeses),
    residualEmPercentual:
      politica === undefined ? '' : formatarPontosBaseComoPercentual(politica.residualEmPontosBase),
    custoDeCapitalEmPercentual:
      politica === undefined
        ? ''
        : formatarPontosBaseComoPercentual(politica.custoDeCapitalAnualEmPontosBase),
    overheadMensal: paraCampoDeReais(politica?.overheadMensal),
  }
}

export interface DialogoDePoliticaDeCustoProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `false` deixa a tela só de leitura — quem tem `cost_policy.read` e não `update`. */
  readonly podeEditar: boolean
}

export function DialogoDePoliticaDeCusto({
  aberto,
  aoFechar,
  podeEditar,
}: DialogoDePoliticaDeCustoProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const consulta = usePoliticaDeCusto()
  const salvar = useSalvarPoliticaDeCusto()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormularioDePolitica>({ defaultValues: valoresIniciais(undefined) })

  const politica = consulta.data

  // O diálogo é montado uma vez e reaproveitado, e a política pode chegar da API
  // DEPOIS da abertura: sem este efeito o formulário ficaria vazio na primeira
  // vez e mostraria os valores antigos na segunda.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(politica))
    }
  }, [aberto, politica, reset])

  const ocupado = salvar.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const residual = percentualParaPontosBase(valores.residualEmPercentual)
    const capital = percentualParaPontosBase(valores.custoDeCapitalEmPercentual)
    const overheadWire = deCampoDeReaisParaWire(valores.overheadMensal)

    if (residual === null) {
      setError('residualEmPercentual', { type: 'validate', message: PERCENTUAL_INVALIDO })
      return
    }

    if (capital === null) {
      setError('custoDeCapitalEmPercentual', { type: 'validate', message: PERCENTUAL_INVALIDO })
      return
    }

    if (overheadWire === null) {
      setError('overheadMensal', {
        type: 'validate',
        message: 'valor inválido — use o formato 8.500,00',
      })
      return
    }

    const entrada = {
      vidaUtilEmMeses: valores.vidaUtilEmMeses,
      residualEmPontosBase: residual,
      custoDeCapitalAnualEmPontosBase: capital,
      overheadMensal: overheadWire,
    }

    const validado = salvarPoliticaDeCustoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    // `entrada`, e não `validado.data`: o schema transforma `overheadMensal` em
    // `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
    salvar.mutate(entrada, {
      onSuccess: () => {
        toast.success('Política de custo salva', {
          description: 'Vale a partir do próximo fechamento. Competências já fechadas não mudam.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Política de custo indireto</DialogTitle>
          <DialogDescription>
            As premissas de depreciação, custo de capital e overhead da frota. O fechamento mensal
            calcula em cima delas.
          </DialogDescription>
        </DialogHeader>

        {consulta.isPending ? (
          <LoadingState rotulo="Carregando a política…" linhas={2} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar a política"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : (
          <form
            noValidate
            onSubmit={(evento) => {
              void aoEnviar(evento)
            }}
            className="space-y-4"
          >
            {erroGeral === null ? null : (
              <Alert variant="danger">
                <AlertDescription>{erroGeral}</AlertDescription>
              </Alert>
            )}

            {/* Sem `cost_policy.update` não existe botão de salvar; dizer isso
                em uma linha evita que a pessoa preencha os quatro campos para
                descobrir no fim que não há onde clicar. */}
            {podeEditar ? null : (
              <Alert variant="info">
                <AlertDescription>
                  Você pode consultar as premissas, mas não alterá-las.
                </AlertDescription>
              </Alert>
            )}

            {politica !== undefined && !politica.definida ? (
              <Alert variant="warning">
                <AlertTitle>Nenhuma política salva ainda</AlertTitle>
                <AlertDescription>
                  Os números abaixo são a sugestão padrão da frota, não uma decisão. O fechamento
                  mensal recusa rodar enquanto ninguém salvar esta tela.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Vida útil padrão"
                obrigatorio
                erro={errors.vidaUtilEmMeses?.message}
                dica="Em meses. Quanto tempo o veículo leva para depreciar até o residual."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('vidaUtilEmMeses', {
                      required: 'informe a vida útil em meses',
                    })}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    placeholder="60"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Valor residual"
                obrigatorio
                erro={errors.residualEmPercentual?.message}
                dica="Em % do valor de compra. É o que sobra no fim da vida útil e NÃO deprecia."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('residualEmPercentual', { validate: validarPercentual })}
                    inputMode="decimal"
                    placeholder="20"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Custo de capital"
                obrigatorio
                erro={errors.custoDeCapitalEmPercentual?.message}
                dica="Em % ao ano. O que o dinheiro parado no veículo renderia se estivesse aplicado."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('custoDeCapitalEmPercentual', { validate: validarPercentual })}
                    inputMode="decimal"
                    placeholder="12"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Overhead mensal"
                obrigatorio
                erro={errors.overheadMensal?.message}
                dica="Em reais. Pátio, equipe e sistemas — rateado entre os veículos ativos do mês."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('overheadMensal', {
                      required: 'informe o overhead mensal',
                      validate: (valor: string) =>
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 8.500,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>
            </div>

            <Alert variant="info">
              <AlertDescription>
                Salvar vale para o PRÓXIMO fechamento. As competências já fechadas continuam com a
                premissa que tinham — histórico contábil não se reescreve sozinho.
              </AlertDescription>
            </Alert>

            {politica?.atualizadoEm == null ? null : (
              <p className="text-xs tabular-money text-fg-muted">
                Última alteração em {formatarDataHora(politica.atualizadoEm)}.
              </p>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
                {podeEditar ? 'Cancelar' : 'Fechar'}
              </Button>

              {podeEditar ? (
                <Button type="submit" disabled={ocupado}>
                  {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                  Salvar política
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
